import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { DescansoMedico } from '../types/descansoMedico';
import { DescansoMedicoService } from '../services/descansoMedicoService';

interface DescansoMedicoStore {
  descansos: DescansoMedico[];
  loading: boolean;
  error: string | null;
  selectedPeriodoId: number | null;

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSelectedPeriodoId: (id: number | null) => void;

  fetchByPeriodo: (idPeriodo: number, idTrabajador?: number) => Promise<void>;
  createDescanso: (descanso: Partial<DescansoMedico>) => Promise<DescansoMedico>;
  updateDescanso: (id: number, descanso: Partial<DescansoMedico>) => Promise<DescansoMedico>;
  deleteDescanso: (id: number) => Promise<void>;
}

export const useDescansoMedicoStore = create<DescansoMedicoStore>()(
  devtools(
    (set, get) => ({
      descansos: [],
      loading: false,
      error: null,
      selectedPeriodoId: null,

      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      setSelectedPeriodoId: (id) => set({ selectedPeriodoId: id }),

      fetchByPeriodo: async (idPeriodo, idTrabajador) => {
        set({ loading: true, error: null });
        try {
          const descansos = await DescansoMedicoService.getByPeriodo(idPeriodo, idTrabajador);
          set({ descansos, selectedPeriodoId: idPeriodo });
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Error al cargar descansos' });
        } finally {
          set({ loading: false });
        }
      },

      createDescanso: async (descanso) => {
        set({ loading: true, error: null });
        try {
          const created = await DescansoMedicoService.create(descanso);
          const periodoId = get().selectedPeriodoId;
          if (periodoId) {
            const descansos = await DescansoMedicoService.getByPeriodo(periodoId);
            set({ descansos });
          }
          return created;
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Error al crear descanso';
          set({ error: msg });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      updateDescanso: async (id, descanso) => {
        set({ loading: true, error: null });
        try {
          const updated = await DescansoMedicoService.update(id, descanso);
          const periodoId = get().selectedPeriodoId;
          if (periodoId) {
            const descansos = await DescansoMedicoService.getByPeriodo(periodoId);
            set({ descansos });
          }
          return updated;
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Error al actualizar descanso';
          set({ error: msg });
          throw err;
        } finally {
          set({ loading: false });
        }
      },

      deleteDescanso: async (id) => {
        set({ loading: true, error: null });
        try {
          await DescansoMedicoService.delete(id);
          const periodoId = get().selectedPeriodoId;
          if (periodoId) {
            const descansos = await DescansoMedicoService.getByPeriodo(periodoId);
            set({ descansos });
          }
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Error al eliminar descanso' });
          throw err;
        } finally {
          set({ loading: false });
        }
      },
    }),
    { name: 'DescansoMedicoStore' }
  )
);
